import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';

type UseControllableStateProps<T> = {
  prop?: T;
  defaultProp: T;
  onChange?: (value: T) => void;
};

type ChangeHandler<T> = (state: T) => void;

export const useControllableState = <T>({
  prop,
  defaultProp,
  onChange,
}: UseControllableStateProps<T>) => {
  const [uncontrolledValue, setUncontrolledValue, onChageRef] =
    useUncontrolledState({
      defaultProp,
      onChange,
    });

  const isControlled = prop !== undefined;
  const value = isControlled ? prop : uncontrolledValue;

  const setValue = useCallback(
    (nextValue: T | ((prev: T) => T)) => {
      if (isControlled) {
        const value =
          typeof nextValue === 'function'
            ? (nextValue as (prev: T) => T)(prop!)
            : nextValue;
        if (value !== prop) {
          onChageRef.current?.(value);
        }
      } else {
        setUncontrolledValue(nextValue);
      }
    },
    [isControlled, prop, onChageRef, setUncontrolledValue],
  );

  return [value, setValue] as const;
};

const useUncontrolledState = <T>({
  defaultProp: defaultValue,
  onChange,
}: Omit<UseControllableStateProps<T>, 'prop'>): [
  Value: T,
  setValue: React.Dispatch<React.SetStateAction<T>>,
  OnChangeRef: React.RefObject<ChangeHandler<T> | undefined>,
] => {
  const [value, setValue] = useState(defaultValue);
  const prevValueRef = useRef(value);

  const onChangeRef = useRef(onChange);

  useLayoutEffect(() => {
    onChangeRef.current = onChange;
  }, [onChange]);

  useEffect(() => {
    if (prevValueRef.current !== value) {
      onChangeRef.current?.(value);
      prevValueRef.current = value;
    }
  }, [value, prevValueRef]);

  return [value, setValue, onChangeRef] as const;
};
